import { useCreateCheckoutSession } from '@medizzy/payments';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { CheckIcon, GraduationCapIcon } from 'lucide-react';
import { type ComponentProps, useEffect, useState } from 'react';
import { useTracking } from 'react-tracking';
import { toast } from 'sonner';
import { Button } from '~/components/button';
import type { Drawer } from '~/components/drawer';
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
} from '~/components/responsive-dialog';
import { VStack } from '~/components/stack';
import { env } from '~/config/env';
import { cn } from '~/utils/cn';
import { getExamName, getExamQuestionCount } from '~/utils/utils';

const PRICES = {
  usmle: {
    monthly: {
      normal: 49.99,
      promo: 24.99,
    },
    annual: {
      normal: 39.99,
      promo: 19.99,
    },
  },
  ems: {
    monthly: {
      normal: 29.99,
      promo: 14.99,
    },
    annual: {
      normal: 19.99,
      promo: 9.99,
    },
  },
  msp: {
    monthly: {
      normal: 9.99,
      promo: 4.99,
    },
    annual: {
      normal: 7.49,
      promo: 3.75,
    },
    oneTimePayment: {
      normal: 5.49,
    },
  },
  mcat: {
    monthly: {
      normal: 9.99,
      promo: 4.99,
    },
    annual: {
      normal: 7.49,
      promo: 3.75,
    },
    oneTimePayment: {
      normal: 5.49,
    },
  },
};

const FEATURES = [
  `Full access to ${getExamQuestionCount()}+ Exam-Style Questions`,
  'All topic decks unlocked',
  'Detailed Explanations & Rationales',
  'Personalized Study Sessions',
  'Customizable Practice Modes',
  'Performance analytics & topic mastery',
  'Pearls, Key Info & more',
] as const;

export function PaywallDialog(props?: ComponentProps<typeof Drawer>) {
  const [selectedPlan, setSelectedPlan] = useState<'yearly' | 'monthly'>(
    'monthly',
  );
  const createCheckoutSession = useCreateCheckoutSession({
    onSuccess: ({ url }) => {
      location.replace(url);
    },
    onError: () => {
      toast.error('We could not create a checkout session. Please try again!');
    },
  });
  const { trackEvent } = useTracking();

  useEffect(() => {
    if (props?.open) {
      trackEvent({ event: 'premium_dialog_open' });
    }
  }, [props?.open, trackEvent]);

  return (
    <ResponsiveDialog dismissible={false} {...props}>
      <ResponsiveDialogContent
        withCloseButton={false}
        className="lg:max-w-[400px]"
      >
        <ResponsiveDialogBody>
          <VStack className="gap-4 justify-center items-center pt-8 pb-4">
            <div className="bg-amber-400 text-amber-950 p-5 rounded-xl">
              <GraduationCapIcon />
            </div>
            <h2 className="text-2xl font-bold text-center">
              Upgrade to Premium
            </h2>
            <p className="text-center text-muted-foreground">
              {env.VITE_APP === 'msp' ? (
                <span>
                  Full access to practice questions with instant feedback and
                  explanations.
                </span>
              ) : (
                <span>
                  Get everything you need to crush the {getExamName()}
                </span>
              )}
            </p>
            <ul>
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <CheckIcon size={16} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <RadioGroup.Root
              className="w-full flex flex-col gap-3"
              value={selectedPlan}
              onValueChange={(value) =>
                setSelectedPlan(value as 'yearly' | 'monthly')
              }
            >
              <SubscriptionPlan
                id="monthly"
                title={`$${PRICES[env.VITE_APP].monthly.normal} monthly`}
              />
              <SubscriptionPlan
                id="yearly"
                title={`$${Math.round(PRICES[env.VITE_APP].annual.normal * 12) - 0.01} annually`}
                promo={`Save $${Math.round(PRICES[env.VITE_APP].monthly.normal * 12 - PRICES[env.VITE_APP].annual.normal * 12)}`}
                variant="deal"
              />
            </RadioGroup.Root>
          </VStack>
        </ResponsiveDialogBody>
        <ResponsiveDialogFooter>
          <VStack className="w-full gap-3">
            <Button
              isLoading={createCheckoutSession.isPending}
              className="flex-1"
              variant="premium"
              onClick={() => {
                trackEvent({ event: 'premium_dialog_buy' });
                createCheckoutSession.mutate({
                  period: selectedPlan,
                });
              }}
            >
              Upgrade
            </Button>
            <ResponsiveDialogClose asChild>
              <Button
                className="w-full"
                variant="ghost"
                onClick={() => {
                  trackEvent({ event: 'premium_dialog_cancel' });
                }}
              >
                Cancel
              </Button>
            </ResponsiveDialogClose>
          </VStack>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

interface SubscriptionPlanProps {
  id: 'yearly' | 'monthly';
  title: string;
  promo?: string;
  variant?: 'default' | 'deal';
}

function SubscriptionPlan({
  id,
  title,
  promo,
  variant = 'default',
}: SubscriptionPlanProps) {
  return (
    <RadioGroup.Item
      id={id}
      value={id}
      className={cn(
        'relative flex items-center border-2 w-full p-4 rounded-lg data-[state=checked]:border-amber-400 transition-colors cursor-pointer',
        {
          'mt-3.5': variant === 'deal',
        },
      )}
    >
      {variant === 'deal' && (
        <span className="text-sm bg-border py-1 px-2 rounded-lg font-semibold absolute -top-3.5 end-4">
          Best Choice
        </span>
      )}
      <span className="flex flex-col text-start gap-3 flex-1">
        <span className="flex flex-col">
          <label htmlFor={id} className="font-semibold">
            {title}
          </label>
          <span className="text-sm text-emerald-700 font-semibold">
            {promo}
          </span>
        </span>
        <span className="text-xs text-muted-foreground">
          Recurring billing. Cancel any time
        </span>
      </span>
      <div className="w-4 h-4 relative border-2 rounded-full flex items-center justify-center">
        <RadioGroup.Indicator className="after:block after:size-[12px] after:rounded-full after:bg-amber-400" />
      </div>
    </RadioGroup.Item>
  );
}
